import { useState, useRef, useEffect, useMemo } from 'react'
import { Search, User, Users, Calendar, FileText, X, Link } from 'lucide-react'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useGlobalSearch, type SearchResult, type SearchResultType } from '@/hooks/use-global-search'
import { useContact } from '@/stores/contacts.store'
import { useLeadsList } from '@/stores/leads.store'
import { useEventsList } from '@/stores/events.store'
import { useInvoicesList } from '@/stores/invoices.store'
import { formatFullName } from '@/lib/utils'
import { Button } from '@/components/ui/button'

const searchTypeToParentType: Record<SearchResultType, ParentTypeEnum> = {
  contact: ParentTypeEnum.PARENT_TYPE_CONTACT,
  lead: ParentTypeEnum.PARENT_TYPE_INQUIRY,
  event: ParentTypeEnum.PARENT_TYPE_EVENT,
  invoice: ParentTypeEnum.PARENT_TYPE_INVOICE,
}

const parentTypeToSearchType: Partial<Record<ParentTypeEnum, SearchResultType>> = {
  [ParentTypeEnum.PARENT_TYPE_CONTACT]: 'contact',
  [ParentTypeEnum.PARENT_TYPE_INQUIRY]: 'lead',
  [ParentTypeEnum.PARENT_TYPE_EVENT]: 'event',
  [ParentTypeEnum.PARENT_TYPE_INVOICE]: 'invoice',
}

const categoryConfig: Record<SearchResultType, { label: string; singularLabel: string; icon: React.ComponentType<{ className?: string }> }> = {
  contact: { label: 'Contacts', singularLabel: 'Contact', icon: User },
  lead: { label: 'Leads', singularLabel: 'Lead', icon: Users },
  event: { label: 'Events', singularLabel: 'Event', icon: Calendar },
  invoice: { label: 'Invoices', singularLabel: 'Invoice', icon: FileText },
}

interface EntityLinkerValue {
  parentType: ParentTypeEnum
  parentId: string
}

interface EntityLinkerProps {
  value: EntityLinkerValue | null
  onChange: (value: EntityLinkerValue | null) => void
  disabled?: boolean
}

function useEntityDisplayName(value: EntityLinkerValue | null): string | null {
  const selectedContactId = value?.parentType === ParentTypeEnum.PARENT_TYPE_CONTACT
    ? value.parentId
    : undefined
  const { contact } = useContact(selectedContactId)
  const { inquiries } = useLeadsList()
  const { events } = useEventsList()
  const { invoices } = useInvoicesList()

  return useMemo(() => {
    if (!value) return null

    switch (value.parentType) {
      case ParentTypeEnum.PARENT_TYPE_CONTACT: {
        return contact?.id === value.parentId
          ? formatFullName(contact.firstName, contact.lastName)
          : null
      }
      case ParentTypeEnum.PARENT_TYPE_INQUIRY: {
        const inquiry = inquiries.find(i => i.id === value.parentId)
        return inquiry ? formatFullName(inquiry.contactFirstName, inquiry.contactLastName) : null
      }
      case ParentTypeEnum.PARENT_TYPE_EVENT: {
        const event = events.find(e => e.id === value.parentId)
        return event ? formatFullName(event.contactFirstName, event.contactLastName) : null
      }
      case ParentTypeEnum.PARENT_TYPE_INVOICE: {
        const invoice = invoices.find(i => i.id === value.parentId)
        return invoice ? (invoice.title || `Invoice #${invoice.invoiceNumber}`) : null
      }
      default:
        return null
    }
  }, [value, contact, inquiries, events, invoices])
}

export function EntityLinker({ value, onChange, disabled }: EntityLinkerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { results } = useGlobalSearch({
    query,
    enabled: open,
    fetchEnabled: open,
    maxResultsPerCategory: 5,
  })

  const displayName = useEntityDisplayName(value)
  const searchType = value ? parentTypeToSearchType[value.parentType] : null
  const config = searchType ? categoryConfig[searchType] : null

  const handleSelect = (result: SearchResult) => {
    onChange({
      parentType: searchTypeToParentType[result.type],
      parentId: result.id,
    })
    setOpen(false)
    setQuery('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  // Focus input when dropdown opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const categories = useMemo(() => {
    const resultsByType: Record<SearchResultType, SearchResult[]> = {
      contact: results.contacts,
      lead: results.leads,
      event: results.events,
      invoice: results.invoices,
    }
    const order: SearchResultType[] = ['contact', 'lead', 'event', 'invoice']
    return order
      .filter(type => resultsByType[type].length > 0)
      .map(type => ({
        type,
        config: categoryConfig[type],
        results: resultsByType[type],
      }))
  }, [results])

  const hasResults = categories.length > 0
  const showEmpty = query.length >= 2 && !hasResults

  return (
    <div className="grid gap-3" ref={containerRef}>
      <label className="text-sm font-medium leading-none">Related to</label>
      <div className="relative">
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
          onClick={() => setOpen(!open)}
        >
          {value && config && displayName ? (
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <config.icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate">{displayName}</span>
              <span className="text-xs text-muted-foreground shrink-0">
                {config.singularLabel}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Link className="h-4 w-4" />
              Link to a contact, lead, event...
            </span>
          )}
          {value ? (
            <span
              role="button"
              tabIndex={0}
              className="ml-2 shrink-0 rounded-sm p-0.5 hover:bg-accent"
              onClick={handleClear}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e as any) }}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          ) : null}
        </Button>

        {open && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border bg-popover shadow-lg">
            {/* Search input */}
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                className="flex h-9 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                placeholder="Search contacts, leads, events..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {/* Scrollable results */}
            <div className="max-h-[300px] overflow-y-auto">
              {query.length < 2 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Type to search...
                </div>
              ) : showEmpty ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No results found.
                </div>
              ) : (
                <div className="py-1">
                  {categories.map(({ type, config: catConfig, results: catResults }) => (
                    <div key={type} className="px-1 py-1">
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        {catConfig.label}
                      </div>
                      {catResults.map((result) => {
                        const Icon = catConfig.icon
                        return (
                          <button
                            key={result.id}
                            type="button"
                            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                            onClick={() => handleSelect(result)}
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 overflow-hidden">
                              <div className="truncate font-medium">{result.title}</div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="truncate">{result.subtitle}</span>
                                {result.metadata && (
                                  <>
                                    <span className="text-muted-foreground/50">·</span>
                                    <span className="truncate">{result.metadata}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
